import CloseIcon from '@mui/icons-material/Close';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import {
  Box,
  Typography,
  IconButton,
  useTheme,
  Skeleton,
  Alert,
  Paper,
  ButtonGroup,
  TextField,
} from '@mui/material';
import { useIntl } from 'react-intl';

import { Button } from '@verantos-dev/shared-components';

import { useDataSplitting } from './useDataSplitting';
import { DATA_IMPORT_MODES } from '../../constants';

interface DataSplittingProps {
  setRequiredFieldsFilled: (fieldsFilled: boolean) => void;
}

export const DataSplitting = ({ setRequiredFieldsFilled }: DataSplittingProps) => {
  const theme = useTheme();
  const { formatMessage } = useIntl();
  const {
    uploadedDatasplittingJSONFileName,
    handleRemoveFile,
    handleClick,
    handleDrop,
    isUploadDatasplittingJSONFileLoading,
    inputRef,
    handleUploadDatasplittingJSONFile,
    uploadError,

    datasplitImportMode,
    setDatasplitMode,

    uploadedDatasplitS3FilePath,
    datasplitS3Input,
    setDatasplitS3Input,

    handleSetDatasplitS3Path,
    handleRemoveDatasplitS3Path,

    datasplitS3Error,
    isSetDatasplitS3PathLoading,
  } = useDataSplitting({ setRequiredFieldsFilled });

  return (
    <Paper
      sx={{
        p: theme.spacing(6),
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(6),
      }}
    >
      <Typography variant="h6">
        {formatMessage({ id: 'createProjectWizard.step.splittingAndDriftCheck.title' })}
      </Typography>

      <ButtonGroup
        color="inherit"
        orientation="horizontal"
        variant="contained"
        sx={{ borderRadius: 0, width: '100%' }}
      >
        {DATA_IMPORT_MODES.map((mode) => {
          const isActive = datasplitImportMode === mode;

          return (
            <Button
              key={mode}
              id={`${mode}-dataset`}
              displayLabel={formatMessage({
                id: `createProjectWizard.step.datasetImport.importMode.${mode}`,
              })}
              variant="contained"
              onClickHandler={() => setDatasplitMode(mode)}
              sx={{
                textTransform: 'none',
                fontSize: '14px',
                flex: 1,
                backgroundColor: isActive ? theme.palette.primary.main : theme.palette.grey[200],
                color: isActive ? theme.palette.white.main : theme.palette.text.primary,
                '&:hover': {
                  backgroundColor: isActive ? theme.palette.primary.dark : theme.palette.grey[300],
                },
                '&:focus': {
                  backgroundColor: isActive ? theme.palette.primary.main : theme.palette.grey[200],
                },
              }}
            />
          );
        })}
      </ButtonGroup>

      {datasplitImportMode === DATA_IMPORT_MODES[0] ? (
        <>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: theme.spacing(1) }}>
            <Typography variant="h6">
              {formatMessage({ id: 'createProjectWizard.step.splittingAndDriftCheck.header' })}
            </Typography>
            <Typography variant="body2" sx={{ color: 'error.main', alignSelf: 'start' }}>
              *
            </Typography>
          </Box>

          {uploadedDatasplittingJSONFileName ? (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                py: theme.spacing(2),
                px: theme.spacing(4),
                border: '1px solid',
                borderColor: theme.palette.divider,
                borderRadius: 2,
                backgroundColor: '#f5f7fb',
              }}
            >
              <Typography variant="body1" sx={{ flex: 1 }}>
                {uploadedDatasplittingJSONFileName}
              </Typography>

              <IconButton onClick={handleRemoveFile} size="small" sx={{ color: 'error.main' }}>
                <CloseIcon />
              </IconButton>
            </Box>
          ) : (
            <Box
              onClick={handleClick}
              onDrop={handleDrop}
              onDragOver={(e) => e.preventDefault()}
              sx={{
                border: '2px dashed',
                borderColor: theme.palette.divider,
                '&:hover': {
                  borderColor: theme.palette.primary.light,
                },
                borderRadius: 2,
                p: 6,
                textAlign: 'center',
                backgroundColor: theme.palette.background.paper,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {isUploadDatasplittingJSONFileLoading ? (
                <Skeleton variant="text" width="100%" height={20} />
              ) : (
                <>
                  <Box display="flex" alignItems="center" gap={1}>
                    <FileUploadIcon />

                    <Typography variant="body1" color="text.secondary">
                      {formatMessage({
                        id: 'createProjectWizard.step.splittingAndDriftCheck.description',
                      })}
                    </Typography>
                  </Box>

                  <input
                    hidden
                    type="file"
                    accept=".json"
                    ref={inputRef}
                    onChange={(event) =>
                      handleUploadDatasplittingJSONFile(event.target.files?.[0] ?? null)
                    }
                  />
                </>
              )}
            </Box>
          )}
          {uploadError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {uploadError}
            </Alert>
          )}
        </>
      ) : datasplitImportMode === DATA_IMPORT_MODES[1] ? (
        <>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography variant="h6">
              {formatMessage({
                id: 'createProjectWizard.step.splittingAndDriftCheck.s3.header',
              })}
            </Typography>

            {uploadedDatasplitS3FilePath ? (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  py: theme.spacing(2),
                  px: theme.spacing(4),
                  border: '1px solid',
                  borderColor: theme.palette.divider,
                  borderRadius: 2,
                  backgroundColor: '#f5f7fb',
                }}
              >
                <Typography sx={{ flex: 1 }}>{uploadedDatasplitS3FilePath}</Typography>

                <IconButton
                  onClick={() => {
                    setDatasplitS3Input('');
                    handleRemoveDatasplitS3Path();
                  }}
                >
                  <CloseIcon />
                </IconButton>
              </Box>
            ) : (
              <Box display="flex" gap={2}>
                <TextField
                  value={datasplitS3Input}
                  onChange={(e) => setDatasplitS3Input(e.target.value)}
                  sx={{ flex: 1 }}
                  placeholder={formatMessage({
                    id: 'createProjectWizard.step.splittingAndDriftCheck.s3.placeholder',
                  })}
                  disabled={isSetDatasplitS3PathLoading}
                />

                <Button
                  id="save-datasplit-s3"
                  displayLabel={formatMessage({
                    id: 'createProjectWizard.step.datasetImport.s3.upload.button.label',
                  })}
                  onClickHandler={() => handleSetDatasplitS3Path(datasplitS3Input)}
                  disabled={!datasplitS3Input || isSetDatasplitS3PathLoading}
                  sx={{
                    textTransform: 'none',
                    fontSize: '14px',
                    backgroundColor: theme.palette.primary.main,
                  }}
                />
              </Box>
            )}

            {datasplitS3Error && <Alert severity="error">{datasplitS3Error}</Alert>}
          </Box>
        </>
      ) : (
        <></>
      )}
    </Paper>
  );
};
